import apiClient from '@/utils/api/client'
import {
  errorHandle,
  getErrorResponse,
  getSuccessResponse,
  validateResponse,
} from '../utils'
import { DEFAULT_ERROR_DISPLAY_INFO } from '../constants'
import { UnifiedApiStatus, WbffResponse } from '../types'
import {
  ProfitLossInfoBrand,
  CourseWithProfitLoss,
  ProfitLossInfo,
  GetAllAssetsResponsePayload,
  PriceTextColor,
} from './types'
import { BrandID, CourseObject } from '@/types/common/course'
import { getTranslation } from '@/services/wbff/i18n'
import * as Sentry from "@sentry/browser"

const getCourseWordingMap = (courseTranslations: {
  [key: string]: string
}): { [key in BrandID]: any } => {
  return {
    [BrandID.BTC]: {
      divider: {
        text: courseTranslations['btc_dividerText'],
        style: 'yellow',
      },
      description: courseTranslations['btc_description'],
    },
    [BrandID.ETH]: {
      description: courseTranslations['eth_description'],
    },
    [BrandID.INVERSE_BTC]: {
      description: courseTranslations['inverseBtc_description'],
      divider: {
        text: courseTranslations['inverseBtc_dividerText'],
        style: 'yellow',
      },
      isNew: false,
    },
    [BrandID.INVERSE_ETH]: {
      description: courseTranslations['inverseEth_description'],
      isNew: false,
    },
    [BrandID.GOLD]: {
      description: courseTranslations['gold_description'],
      divider: {
        text: courseTranslations['gold_dividerText'],
        style: 'blue',
      },
    },
    [BrandID.TECHNOLOGY]: {
      description: courseTranslations['technology_description'],
    },
    [BrandID.STANDARD]: {
      description: courseTranslations['standard_description'],
    },
    [BrandID.US_LONGTERM_CHALLENGE]: {
      description: courseTranslations['usLongTermChallenge_description'],
      divider: {
        text: courseTranslations['usLongTermChallenge_dividerText'],
        style: 'blue',
      },
    },
    [BrandID.TECH_CHALLENGE]: {
      description: courseTranslations['techChallenge_description'],
    },
    [BrandID.CHALLENGE]: {
      description: courseTranslations['challenge_description'],
    },
    [BrandID.REVERSE_TECH_CHALLENGE]: {
      description: courseTranslations['reverseTechChallenge_description'],
      divider: {
        text: courseTranslations['reverseTechChallenge_dividerText'],
        style: 'blue',
      },
    },
    [BrandID.REVERSE_CHALLENGE]: {
      description: courseTranslations['reverseChallenge_description'],
    },
    [BrandID.NONE]: undefined,
    [BrandID.ALL]: undefined,
  }
}

export const makeCourseList = (
  profitLossInfoBrandList: Array<ProfitLossInfoBrand>,
  courseTranslations: { [key: string]: string },
  tmpBrands?: Array<CourseObject>
): Array<CourseWithProfitLoss> => {
  const allCourses = tmpBrands || g.tmpBrands
  const courseWordingMap = getCourseWordingMap(courseTranslations)

  return allCourses.map((course) => {
    const wording = courseWordingMap[course.BRAND_ID as BrandID]
    const profitLossInfo = profitLossInfoBrandList.find(
      (brand) => brand.BRAND_ID === course.BRAND_ID,
    )?.PROFIT_LOSS_INFO

    return {
      id: course.BRAND_ID,
      icon: course.IMAGE_PATH,
      description: wording?.description,
      name: course.BRAND_NM,
      availableAmount: profitLossInfo ? g.fmtAmount(profitLossInfo.VALUATION_AMOUNT) : '0',
      profitLossAmount:
        profitLossInfo && profitLossInfo.PROFIT_LOSS_AMOUNT !== 0
          ? g.fmtAmount(profitLossInfo.PROFIT_LOSS_AMOUNT, true)
          : '±0',
      profitLossRate: profitLossInfo && profitLossInfo.PROFIT_LOSS_AMOUNT !== 0
        ? g.fmtRate(profitLossInfo.PROFIT_LOSS_RATE, true)
        : '±0.0%',
      profitLossStyle: getProfitLossStyle(profitLossInfo),
      isNew: wording?.isNew,
      divider: wording?.divider
        ? {
            text: wording?.divider?.text,
            style: wording?.divider?.style,
          }
        : null,
      hasBuy: profitLossInfo?.VALUATION_AMOUNT && profitLossInfo.VALUATION_AMOUNT > 0,
    }
  })
}

const getProfitLossStyle = (profitLossInfo: ProfitLossInfo | undefined): string => {
  if (!profitLossInfo || profitLossInfo.PROFIT_LOSS_AMOUNT === 0) return ''

  if (profitLossInfo.PROFIT_LOSS_AMOUNT > 0) return PriceTextColor.GREEN
  else if (profitLossInfo.PROFIT_LOSS_AMOUNT < 0) return PriceTextColor.RED
  else return ''
}

export const getAllAssetsWbff = async (): Promise<WbffResponse<GetAllAssetsResponsePayload>> => {
  try {
    const getUserAssetsAll = await apiClient.get('/v1/user/assets/all')
    const courseTranslations = await getTranslation('course')

    const payload: any = {}
    const userAssetsAllApiStatus = validateResponse(getUserAssetsAll)

    if (userAssetsAllApiStatus === UnifiedApiStatus.Success) {
      payload.courseListWithProfitLoss = makeCourseList(
        getUserAssetsAll.data.PROFIT_LOSS_INFO_BRAND_LIST,
        courseTranslations,
      )
      return getSuccessResponse(payload)
    }
    return errorHandle(userAssetsAllApiStatus, getUserAssetsAll)
  } catch (error) {
    Sentry.captureException(new Error('Exception happened at getAllAssetsWbff'))
    return getErrorResponse(DEFAULT_ERROR_DISPLAY_INFO)
  }
}
